/**
 * Invitation Accept Page
 *
 * Allows users to view and accept team invitations.
 * Handles various states: loading, valid, expired, already accepted, not logged in.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Users,
  Clock,
  LogIn,
  UserPlus,
  Building2,
  Shield,
  XCircle,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../services/authenticatedApi';
import { useJWTAuth } from '../contexts/useJWTAuth';
import { useTenant } from '../contexts/useTenant';
import { logger } from '../utils/logger';
import {
  LoadingCard,
  AcceptedCard,
  ExpiredCard,
  AlreadyAcceptedCard,
  NotFoundCard,
  ErrorCard,
} from './InvitationAcceptPage.states';

type InvitationStatus =
  | 'loading'
  | 'valid'
  | 'expired'
  | 'already_accepted'
  | 'not_found'
  | 'error'
  | 'accepted';

interface InvitationData {
  email: string;
  role: 'admin' | 'member';
  expiresAt: string;
  createdAt: string;
}

interface TenantData {
  id: string;
  name: string;
  slug: string;
}

interface InviterData {
  firstName: string | null;
  email: string;
}

interface InvitationState {
  status: InvitationStatus;
  message: string;
  invitation: InvitationData | null;
  tenant: TenantData | null;
  inviter: InviterData | null;
}

export const InvitationAcceptPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useJWTAuth();
  const { refreshTenants, switchTenant } = useTenant();

  const [state, setState] = useState<InvitationState>({
    status: 'loading',
    message: 'Loading invitation details...',
    invitation: null,
    tenant: null,
    inviter: null,
  });

  const [acceptError, setAcceptError] = useState<string>('');
  const [isAccepting, setIsAccepting] = useState(false);
  const fetchAttempted = useRef(false);
  const redirectTimerRef = useRef<number | null>(null);

  // Fetch invitation details on mount
  useEffect(() => {
    if (token && !fetchAttempted.current) {
      fetchAttempted.current = true;
      fetchInvitationDetails(token);
    }
  }, [token]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const fetchInvitationDetails = async (invitationToken: string) => {
    try {
      setState((prev) => ({
        ...prev,
        status: 'loading',
        message: 'Loading invitation details...',
      }));

      const response = await api
        .get(`/tenants/invitations/${invitationToken}`)
        .catch((err) => err.response);
      const data = response?.data;

      if (!response || response.status >= 400) {
        if (data?.code === 'INVITATION_NOT_FOUND') {
          setState({
            status: 'not_found',
            message: data.message || 'This invitation link is invalid or has already been used.',
            invitation: null,
            tenant: null,
            inviter: null,
          });
        } else if (data?.code === 'INVITATION_EXPIRED') {
          setState({
            status: 'expired',
            message: data.message || 'This invitation has expired.',
            invitation: null,
            tenant: data.tenant || null,
            inviter: null,
          });
        } else if (data?.code === 'INVITATION_ALREADY_ACCEPTED') {
          setState({
            status: 'already_accepted',
            message: data.message || 'This invitation has already been used.',
            invitation: null,
            tenant: data.tenant || null,
            inviter: null,
          });
        } else {
          setState({
            status: 'error',
            message: data?.error || 'Failed to load invitation details.',
            invitation: null,
            tenant: null,
            inviter: null,
          });
        }
        return;
      }

      setState({
        status: 'valid',
        message: '',
        invitation: data.invitation,
        tenant: data.tenant,
        inviter: data.inviter,
      });
    } catch (error) {
      logger.error('Failed to fetch invitation', { error });
      setState({
        status: 'error',
        message: 'Failed to load invitation details. Please try again.',
        invitation: null,
        tenant: null,
        inviter: null,
      });
    }
  };

  const handleAcceptInvitation = async () => {
    if (!token || !isAuthenticated) return;

    try {
      setIsAccepting(true);
      setAcceptError('');

      const response = await api
        .post(`/tenants/invitations/${token}/accept`)
        .catch((err) => err.response);
      const data = response?.data;

      if (!response || response.status >= 400) {
        setAcceptError(data?.message || data?.error || 'Failed to accept invitation.');
        setIsAccepting(false);
        return;
      }

      setState((prev) => ({
        ...prev,
        status: 'accepted',
        message: data.message || 'Successfully joined the team!',
      }));

      // Refresh tenants list and switch to the new tenant
      await refreshTenants();
      if (data.tenant?.id) {
        await switchTenant(data.tenant.id);
      }

      // Redirect to team settings after a brief delay
      redirectTimerRef.current = window.setTimeout(() => {
        navigate('/settings/team', { replace: true });
      }, 2000);
    } catch (error) {
      logger.error('Failed to accept invitation', { error });
      setAcceptError('Failed to accept invitation. Please try again.');
      setIsAccepting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRoleBadgeColor = (role: string) => {
    return role === 'admin'
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <main className="grow flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="grow flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {state.status === 'loading' && <LoadingCard message={state.message} />}

          {/* Valid Invitation */}
          {state.status === 'valid' && state.invitation && state.tenant && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
              <div className="bg-linear-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center">
                <Building2 className="w-16 h-16 text-white/90 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">
                  You&apos;ve been invited to join
                </h2>
                <p className="text-3xl font-bold text-white">{state.tenant.name}</p>
              </div>

              <div className="p-6 space-y-6">
                {/* Invitation Details */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Role
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getRoleBadgeColor(state.invitation.role)}`}
                    >
                      {state.invitation.role}
                    </span>
                  </div>

                  {state.inviter && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        Invited by
                      </span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {state.inviter.firstName || state.inviter.email}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Expires
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(state.invitation.expiresAt)}
                      <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">
                        ({getDaysRemaining(state.invitation.expiresAt)} days)
                      </span>
                    </span>
                  </div>
                </div>

                {/* Email Mismatch Warning */}
                {isAuthenticated &&
                  user?.email &&
                  state.invitation.email.toLowerCase() !== user.email.toLowerCase() && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                            Email mismatch
                          </p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            This invitation was sent to <strong>{state.invitation.email}</strong>,
                            but you&apos;re logged in as <strong>{user.email}</strong>. You can
                            still accept it if this is your account.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Accept Error */}
                {acceptError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex gap-3">
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />
                      <p className="text-sm text-red-700 dark:text-red-300">{acceptError}</p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {isAuthenticated ? (
                  <button
                    onClick={handleAcceptInvitation}
                    disabled={isAccepting}
                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isAccepting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Joining...
                      </>
                    ) : (
                      <>
                        <Users className="w-5 h-5" />
                        Accept Invitation
                      </>
                    )}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-center text-gray-600 dark:text-gray-400 text-sm">
                      Sign in or create an account to accept this invitation
                    </p>
                    <div className="flex gap-3">
                      <Link
                        to={`/signin?redirect=/invitations/${token}`}
                        className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <LogIn className="w-5 h-5" />
                        Sign In
                      </Link>
                      <Link
                        to={`/signup?redirect=/invitations/${token}&email=${encodeURIComponent(state.invitation.email)}`}
                        className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <UserPlus className="w-5 h-5" />
                        Sign Up
                      </Link>
                    </div>
                  </div>
                )}

                <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                  By accepting, you&apos;ll join as {state.invitation.role === 'admin' ? 'an' : 'a'}{' '}
                  {state.invitation.role} and gain access to team resources.
                </p>
              </div>
            </div>
          )}

          {state.status === 'accepted' && <AcceptedCard message={state.message} />}

          {state.status === 'expired' && (
            <ExpiredCard message={state.message} tenant={state.tenant} />
          )}

          {state.status === 'already_accepted' && (
            <AlreadyAcceptedCard message={state.message} tenant={state.tenant} />
          )}

          {state.status === 'not_found' && <NotFoundCard message={state.message} />}

          {state.status === 'error' && (
            <ErrorCard
              message={state.message}
              onRetry={() => token && fetchInvitationDetails(token)}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InvitationAcceptPage;
